import { useEffect, useState, useRef } from 'react';
import { createOrFetchUser } from '../service';

const BALANCE_POLLING_INTERVAL = 30000; // 30 seconds

// This hook mounts simultaneously in Header, SideNav and TrackingDetails. Coalesce
// concurrent POST /v1/auth/users calls (keyed by email) into a single in-flight
// request so their 30s poll ticks share one request instead of firing three.
const inFlightByEmail = new Map();
const fetchUserOnce = (email, method, shouldSubscribe) => {
  if (inFlightByEmail.has(email)) return inFlightByEmail.get(email);
  const promise = createOrFetchUser(email, method, shouldSubscribe)
    .finally(() => inFlightByEmail.delete(email));
  inFlightByEmail.set(email, promise);
  return promise;
};

const useUserBalance = (isSignedIn, userData) => {
  const [balance, setBalance] = useState(null);
  const intervalRef = useRef(null);

  const fetchBalance = async () => {
    if (!isSignedIn || !userData) return;

    try {
      const { data } = await fetchUserOnce(
        userData.email,
        userData.method,
        userData.shouldSubscribe,
      );
      setBalance(data.balance?.currentBalance);
    } catch (error) {
      console.error('Error fetching user balance:', error);
    }
  };

  useEffect(() => {
    if (!isSignedIn || !userData) {
      setBalance(null);
      return undefined;
    }

    // FirebaseProvider already fetched the balance into userData on auth (and on
    // every reloadUserData), so seed from it instead of POSTing /v1/auth/users
    // again on mount — that duplicate was the remaining redundant call per load.
    // Freshness is preserved by the 30s poll below.
    setBalance(userData.balance?.currentBalance ?? null);

    intervalRef.current = setInterval(fetchBalance, BALANCE_POLLING_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isSignedIn, userData]);

  return balance;
};

export default useUserBalance;
